import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Tag, Percent, DollarSign, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);
const categoryNames = {
  'tortas-especiales': 'Tortas Especiales',
  'tortas-personalizadas': 'Tortas Personalizadas',
  'tortas-tematicas': 'Tortas Temáticas',
  'mesa-dulces': 'Mesa de Dulces',
  'detalles-regalos': 'Detalles y Regalos',
  'tortas': 'Tortas',
  'refrigeradas': 'Refrigeradas',
  'clasicas': 'Clasicas',
  'saludables': 'Saludables',
  'pide-para-hoy': 'Pide para Hoy',
  'panaderia': 'Panadería',
  'bebidas': 'Bebidas',
  'cafeteria': 'Cafetería'
};

const emptyPromo = { name: '', description: '', discount_type: 'percentage', discount_value: 0, min_order: 0, applicable_categories: [], code: '', active: true, start_date: '', end_date: '' };

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPromo);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promotions`, { withCredentials: true, headers: getHeaders() });
      setPromos(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPromos(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyPromo); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', discount_type: p.discount_type, discount_value: p.discount_value, min_order: p.min_order || 0, applicable_categories: p.applicable_categories || [], code: p.code || '', active: p.active, start_date: p.start_date || '', end_date: p.end_date || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/promotions/${editing.id}`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Promocion actualizada');
      } else {
        await axios.post(`${API}/admin/promotions`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Promocion creada');
      }
      setDialogOpen(false);
      fetchPromos();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta promocion?')) return;
    try {
      await axios.delete(`${API}/admin/promotions/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Eliminada');
      fetchPromos();
    } catch { toast.error('Error'); }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/admin/promotions/${promo.id}`, { active: !promo.active }, { withCredentials: true, headers: getHeaders() });
      fetchPromos();
    } catch { toast.error('Error'); }
  };

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div data-testid="admin-promotions-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-[#46371c]">Promociones</h1>
        <Button onClick={openCreate} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="add-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Promocion
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
      ) : promos.length === 0 ? (
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-12 text-center">
          <Tag className="w-12 h-12 mx-auto text-[#e5dfd3] mb-4" />
          <p className="font-body text-[#75664d] mb-4">No hay promociones creadas</p>
          <Button onClick={openCreate} className="bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full font-body" data-testid="create-first-promo-btn">
            Crear primera promocion
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="promos-grid">
          {promos.map(promo => (
            <Card key={promo.id} className={`border rounded-2xl overflow-hidden ${promo.active ? 'border-[#a0d5ba] bg-[#fcfbf9]' : 'border-[#e5dfd3] bg-[#f4f1ea] opacity-60'}`} data-testid={`promo-card-${promo.id}`}>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#46371c]">{promo.name}</h3>
                    {promo.code && <Badge className="bg-[#a0d5ba]/20 text-[#46371c] border-[#a0d5ba] text-xs mt-1">{promo.code}</Badge>}
                  </div>
                  <Switch checked={promo.active} onCheckedChange={() => toggleActive(promo)} data-testid={`toggle-promo-${promo.id}`} />
                </div>
                {promo.description && <p className="font-body text-sm text-[#75664d] mb-3">{promo.description}</p>}
                <div className="flex items-center gap-2 mb-3">
                  <div className="bg-[#a0d5ba]/20 rounded-full px-3 py-1 flex items-center gap-1">
                    {promo.discount_type === 'percentage' ? <Percent className="w-3 h-3 text-[#46371c]" /> : <DollarSign className="w-3 h-3 text-[#46371c]" />}
                    <span className="font-body text-sm font-semibold text-[#46371c]">
                      {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : formatPrice(promo.discount_value)}
                    </span>
                  </div>
                  {promo.min_order > 0 && <span className="font-body text-xs text-[#75664d]">Min: {formatPrice(promo.min_order)}</span>}
                </div>
                {(promo.start_date || promo.end_date) && (
                  <p className="font-body text-xs text-[#75664d] flex items-center gap-1 mb-3">
                    <Calendar className="w-3 h-3" />
                    {promo.start_date || '...'} - {promo.end_date || '...'}
                  </p>
                )}
                <div className="font-body text-xs text-[#75664d]">Usado {promo.times_used || 0} veces</div>
                <div className="flex gap-1 mt-3 pt-3 border-t border-[#e5dfd3]">
                  <button onClick={() => openEdit(promo)} className="p-2 hover:bg-[#f4f1ea] rounded-lg" data-testid={`edit-promo-${promo.id}`}><Pencil className="w-4 h-4 text-[#75664d]" /></button>
                  <button onClick={() => handleDelete(promo.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-promo-${promo.id}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{editing ? 'Editar Promocion' : 'Nueva Promocion'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">Configura los detalles de la promocion</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre *</label>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-name-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Descripcion</label>
              <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={2} className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none" data-testid="promo-desc-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Tipo</label>
                <Select value={form.discount_type} onValueChange={v => handleChange('discount_type', v)}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3]" data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje</SelectItem>
                    <SelectItem value="fixed">Monto fijo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Valor</label>
                <input type="number" value={form.discount_value} onChange={e => handleChange('discount_value', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-value-input" />
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Codigo</label>
                <input value={form.code} onChange={e => handleChange('code', e.target.value.toUpperCase())} placeholder="Ej: KEIKS20" className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-code-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Fecha inicio</label>
                <input type="date" value={form.start_date} onChange={e => handleChange('start_date', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-start-input" />
              </div>
              <div>
                <label className="font-body text-sm text-[#75664d] mb-1 block">Fecha fin</label>
                <input type="date" value={form.end_date} onChange={e => handleChange('end_date', e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-end-input" />
              </div>
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Pedido minimo (COP)</label>
              <input type="number" value={form.min_order} onChange={e => handleChange('min_order', Number(e.target.value))} className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="promo-min-order-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-promo-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
